import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useState } from 'react';
import { Lock, MailCheck, ShieldCheck } from 'lucide-react';
import { hardwareApi } from '../../../services/hardwareApi';

/**
 * THE WALL IN FRONT OF CHANGING WHICH DEVICE A CUSTOMER GETS.
 *
 * Request a code, it is emailed to the person asking (with the super admin
 * copied, so nothing happens unseen), enter it, and the unit is changeable —
 * once, for a few minutes, then locked again.
 *
 * NOTHING HERE IS A GUARD. The server refuses the write without a live unlock
 * whatever this renders. What this adds is that an operator meets the wall
 * before typing into it, and gets the server's own reason back — wrong code,
 * expired, attempts exhausted — because each needs a different response from
 * them and flattening all three to "invalid" tells them nothing.
 *
 * Shared by the danger zone and the unit picker so there is ONE code-entry
 * behaviour to reason about; two copies would drift on the details that matter
 * (attempt wording, resend, what happens after success).
 */

const OverrideGate: React.FC<{
  unitId: string;
  /** What the unlock is for, in the operator's words. */
  intent: string;
  onUnlocked: () => void;
}> = ({ unitId, intent, onUnlocked }) => {
  const [stage, setStage] = useState<'locked' | 'sent'>('locked');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestCode = async () => {
    setBusy(true); setError(null);
    try {
      const res = await hardwareApi.requestUnitOverride(unitId);
      setSentTo(res.sentTo);
      setStage('sent');
    } catch (e: any) {
      setError(e?.message || 'Could not send a code.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true); setError(null);
    try {
      await hardwareApi.verifyUnitOverride(unitId, code.trim());
      setCode('');
      onUnlocked();
    } catch (e: any) {
      setError(e?.message || 'That code was not accepted.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-stone-300 bg-stone-50 p-3">
      <div className="flex items-center gap-2">
        <Lock size={13} className="text-stone-500" />
        <span className="text-[12.5px] font-semibold text-stone-800">This device is locked</span>
      </div>

      {stage === 'locked' ? (
        <>
          <p className="mt-1 text-[11.5px] leading-relaxed text-stone-500">
            {intent} needs a verification code. We will email one to you, and copy the super admin so the
            change is never made unseen.
          </p>
          <button
            onClick={requestCode}
            disabled={busy}
            className="mt-2.5 inline-flex min-h-[38px] items-center gap-1.5 rounded-lg border border-stone-400 bg-white px-3 text-[12px] font-semibold text-stone-800 hover:border-[#DC143C] hover:text-[#DC143C] disabled:opacity-50"
          >
            {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <ShieldCheck size={13} />}
            Override — email me a code
          </button>
        </>
      ) : (
        <>
          <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-stone-600">
            <MailCheck size={12} className="text-emerald-600" />
            Code sent to <b className="font-medium">{sentTo}</b>. It lasts 10 minutes and works once.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && code.trim()) verify(); }}
              inputMode="numeric"
              placeholder="000000"
              aria-label="Verification code"
              className="min-h-[38px] w-32 rounded-lg border border-stone-300 px-2.5 text-center font-mono text-[15px] tracking-[0.3em] outline-none focus:border-[#DC143C]"
            />
            <button
              onClick={verify}
              disabled={busy || !code.trim()}
              className="min-h-[38px] rounded-lg px-3.5 text-[12px] font-bold text-white disabled:opacity-40"
              style={{ background: '#DC143C' }}
            >{busy ? 'Checking…' : 'Unlock'}</button>
            <button
              onClick={requestCode}
              disabled={busy}
              className="min-h-[38px] rounded-lg px-2 text-[11.5px] font-semibold text-stone-500 hover:text-stone-800"
            >Resend</button>
          </div>
        </>
      )}

      {error && <p className="mt-2 text-[11.5px] font-medium text-[#DC143C]">{error}</p>}
    </div>
  );
};

export default OverrideGate;
